import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Home, Package, Store, UserRound } from 'lucide-react';
import { useAuth } from '../context/AuthProvider.tsx';
import {
  deleteComercioMarket,
  deleteEmprendedorAdminMarket,
  deleteProductoMarket,
  getComerciosAdminMarket,
  getEmprendedoresAdminMarket,
  getProductosMarket,
  resetPasswordEmprendedorAdminMarket,
  updateComercioMarket,
  updateProductoMarket,
  type ComercioStore,
  type ProductoStore,
} from '../services/marketplaceService.tsx';

type TabKey = 'comercios' | 'productos' | 'emprendedores';
type ConfirmAction = null | {
  title: string;
  message: string;
  confirmText?: string;
  onConfirm: () => Promise<void>;
};

export default function MarketplaceAdmin() {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<TabKey>('comercios');
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [toast, setToast] = useState<string>('');

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState<{ comercioId: number; nombre: string } | null>(null);
  const [newPassword, setNewPassword] = useState('');

  const [rowLoading, setRowLoading] = useState<Record<string, boolean>>({});

  const [comercios, setComercios] = useState<Array<ComercioStore & { productos_total?: number }>>([]);
  const [productos, setProductos] = useState<ProductoStore[]>([]);
  const [emprendedores, setEmprendedores] = useState<ComercioStore[]>([]);

  const [filtroComercios, setFiltroComercios] = useState('');
  const [filtroProductos, setFiltroProductos] = useState('');
  const [filtroEmprendedores, setFiltroEmprendedores] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const [c, p, e] = await Promise.all([
        getComerciosAdminMarket(true),
        getProductosMarket(undefined, true),
        getEmprendedoresAdminMarket(true),
      ]);
      setComercios(c || []);
      setProductos(p || []);
      setEmprendedores(e || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(''), 2400);
    return () => clearTimeout(id);
  }, [toast]);

  const comercioNombre = useMemo(() => {
    const m = new Map<number, string>();
    for (const c of comercios) m.set(Number(c.comercio_id), c.nombre);
    return m;
  }, [comercios]);

  const comerciosFiltrados = useMemo(() => {
    const t = filtroComercios.trim().toLowerCase();
    if (!t) return comercios;
    return comercios.filter((c) =>
      [String(c.comercio_id), c.nombre, c.slug, c.descripcion, c.emprendedor?.nombreMarca, c.emprendedor?.rubro]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(t)
    );
  }, [comercios, filtroComercios]);

  const productosFiltrados = useMemo(() => {
    const t = filtroProductos.trim().toLowerCase();
    if (!t) return productos;
    return productos.filter((p) =>
      [String(p.producto_id), p.nombre, p.descripcion, comercioNombre.get(Number(p.comercio_id))]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(t)
    );
  }, [productos, filtroProductos, comercioNombre]);

  const emprendedoresFiltrados = useMemo(() => {
    const t = filtroEmprendedores.trim().toLowerCase();
    if (!t) return emprendedores;
    return emprendedores.filter((e) =>
      [
        String(e.comercio_id),
        e.nombre,
        e.slug,
        e.emprendedor?.nombreMarca,
        e.emprendedor?.rubro,
        e.emprendedor?.telefono,
        (e as any)?.emprendedor?.cuenta?.email,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(t)
    );
  }, [emprendedores, filtroEmprendedores]);

  const runConfirm = async () => {
    if (!confirmAction) return;
    try {
      setBusy(true);
      await confirmAction.onConfirm();
      setConfirmAction(null);
    } finally {
      setBusy(false);
    }
  };

  const runRowAction = async (key: string, action: () => Promise<void>) => {
    try {
      setRowLoading((prev) => ({ ...prev, [key]: true }));
      await action();
    } finally {
      setRowLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const submitPasswordReset = async () => {
    if (!passwordTarget) return;
    const value = String(newPassword || '').trim();
    if (value.length < 6) {
      setToast('La contraseña debe tener al menos 6 caracteres.');
      return;
    }
    try {
      setBusy(true);
      await resetPasswordEmprendedorAdminMarket(passwordTarget.comercioId, value);
      setShowPasswordModal(false);
      setPasswordTarget(null);
      setNewPassword('');
      setToast('Contraseña actualizada.');
    } finally {
      setBusy(false);
    }
  };

  if (!isAdmin) return <div className="mx-auto max-w-5xl p-6">Solo administradores.</div>;

  return (
    <div className="mx-auto max-w-7xl p-4 space-y-4">
      <nav className="flex" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-2">
          <li>
            <Link to="/inicio" className="inline-flex items-center text-sm text-blue-600">
              <Home className="w-4 h-4 mr-1" /> Inicio
            </Link>
          </li>
          <li className="text-sm text-slate-500">/ Marketplace Admin</li>
        </ol>
      </nav>

      <div className="rounded-xl bg-white p-4 shadow space-y-3">
        <h1 className="text-2xl font-bold">Marketplace · Admin</h1>
        <p className="text-sm text-slate-600">Separado por pestañas: comercios, productos y emprendedores.</p>

        <div className="flex flex-wrap gap-2">
          <button className={`rounded px-3 py-2 text-sm ${tab === 'comercios' ? 'bg-blue-600 text-white' : 'border'}`} onClick={() => setTab('comercios')}>
            <Store className="inline w-4 h-4 mr-1" /> Comercios
          </button>
          <button className={`rounded px-3 py-2 text-sm ${tab === 'productos' ? 'bg-blue-600 text-white' : 'border'}`} onClick={() => setTab('productos')}>
            <Package className="inline w-4 h-4 mr-1" /> Productos
          </button>
          <button className={`rounded px-3 py-2 text-sm ${tab === 'emprendedores' ? 'bg-blue-600 text-white' : 'border'}`} onClick={() => setTab('emprendedores')}>
            <UserRound className="inline w-4 h-4 mr-1" /> Emprendedores
          </button>
        </div>
      </div>

      {tab === 'comercios' && (
        <section className="rounded-xl bg-white p-4 shadow space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-semibold">Comercios ({comerciosFiltrados.length})</h2>
            <input className="rounded border p-2 w-full sm:w-96" placeholder="Filtrar comercios..." value={filtroComercios} onChange={(e) => setFiltroComercios(e.target.value)} />
          </div>

          {loading ? <p className="text-sm text-slate-500">Cargando...</p> : (
            <div className="overflow-auto rounded border">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50"><tr><th className="px-3 py-2 text-left">ID</th><th className="px-3 py-2 text-left">Comercio</th><th className="px-3 py-2 text-left">Productos</th><th className="px-3 py-2 text-left">Estado</th><th className="px-3 py-2 text-left">Acciones</th></tr></thead>
                <tbody>
                  {comerciosFiltrados.map((c) => (
                    <tr key={c.comercio_id} className="border-t">
                      <td className="px-3 py-2">#{c.comercio_id}</td>
                      <td className="px-3 py-2"><div className="font-medium">{c.nombre}</div><div className="text-xs text-slate-500">{c.slug}</div></td>
                      <td className="px-3 py-2">{Number(c.productos_total || 0)}</td>
                      <td className="px-3 py-2">{c.activo ? 'Activo' : 'Inactivo'}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-2">
                          <button
                            className="rounded border px-2 py-1 disabled:opacity-60"
                            disabled={!!rowLoading[`comercio:${c.comercio_id}`]}
                            onClick={() => runRowAction(`comercio:${c.comercio_id}`, async () => {
                              await updateComercioMarket(c.comercio_id, { activo: true });
                              await load();
                            })}
                          >
                            {rowLoading[`comercio:${c.comercio_id}`] ? 'Procesando...' : 'Aprobar'}
                          </button>
                          <button
                            className="rounded border px-2 py-1 disabled:opacity-60"
                            disabled={!!rowLoading[`comercio:${c.comercio_id}`]}
                            onClick={() => runRowAction(`comercio:${c.comercio_id}`, async () => {
                              await updateComercioMarket(c.comercio_id, { activo: false });
                              await load();
                            })}
                          >
                            {rowLoading[`comercio:${c.comercio_id}`] ? 'Procesando...' : 'Quitar'}
                          </button>
                          <button className="rounded bg-red-600 text-white px-2 py-1" onClick={() => setConfirmAction({
                            title: 'Eliminar comercio',
                            message: `¿Seguro que querés eliminar ${c.nombre}? Se borrarán todos sus productos.`,
                            confirmText: 'Sí, eliminar comercio',
                            onConfirm: async () => {
                              await deleteComercioMarket(c.comercio_id);
                              await load();
                              setToast('Comercio eliminado con sus productos.');
                            },
                          })}>Eliminar</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {tab === 'productos' && (
        <section className="rounded-xl bg-white p-4 shadow space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-semibold">Productos ({productosFiltrados.length})</h2>
            <input className="rounded border p-2 w-full sm:w-96" placeholder="Filtrar productos..." value={filtroProductos} onChange={(e) => setFiltroProductos(e.target.value)} />
          </div>

          <div className="overflow-auto rounded border">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50"><tr><th className="px-3 py-2 text-left">ID</th><th className="px-3 py-2 text-left">Producto</th><th className="px-3 py-2 text-left">Comercio</th><th className="px-3 py-2 text-left">Estado</th><th className="px-3 py-2 text-left">Acciones</th></tr></thead>
              <tbody>
                {productosFiltrados.map((p) => (
                  <tr key={p.producto_id} className="border-t">
                    <td className="px-3 py-2">#{p.producto_id}</td>
                    <td className="px-3 py-2">{p.nombre}</td>
                    <td className="px-3 py-2">{comercioNombre.get(Number(p.comercio_id)) || `#${p.comercio_id}`}</td>
                    <td className="px-3 py-2">{p.activo ? 'Activo' : 'Inactivo'}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-2">
                        <button
                          className="rounded border px-2 py-1 disabled:opacity-60"
                          disabled={!!rowLoading[`producto:${p.producto_id}`]}
                          onClick={() => runRowAction(`producto:${p.producto_id}`, async () => {
                            await updateProductoMarket(p.producto_id, { activo: true });
                            await load();
                          })}
                        >
                          {rowLoading[`producto:${p.producto_id}`] ? 'Procesando...' : 'Aprobar'}
                        </button>
                        <button
                          className="rounded border px-2 py-1 disabled:opacity-60"
                          disabled={!!rowLoading[`producto:${p.producto_id}`]}
                          onClick={() => runRowAction(`producto:${p.producto_id}`, async () => {
                            await updateProductoMarket(p.producto_id, { activo: false });
                            await load();
                          })}
                        >
                          {rowLoading[`producto:${p.producto_id}`] ? 'Procesando...' : 'Quitar'}
                        </button>
                        <button className="rounded bg-red-600 text-white px-2 py-1" onClick={() => setConfirmAction({
                          title: 'Eliminar producto',
                          message: `¿Seguro que querés eliminar el producto ${p.nombre}?`,
                          confirmText: 'Sí, eliminar producto',
                          onConfirm: async () => {
                            await deleteProductoMarket(p.producto_id);
                            await load();
                            setToast('Producto eliminado.');
                          },
                        })}>Eliminar</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'emprendedores' && (
        <section className="rounded-xl bg-white p-4 shadow space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-semibold">Emprendedores ({emprendedoresFiltrados.length})</h2>
            <input className="rounded border p-2 w-full sm:w-96" placeholder="Filtrar emprendedores..." value={filtroEmprendedores} onChange={(e) => setFiltroEmprendedores(e.target.value)} />
          </div>

          <div className="overflow-auto rounded border">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50"><tr><th className="px-3 py-2 text-left">ID</th><th className="px-3 py-2 text-left">Marca</th><th className="px-3 py-2 text-left">Usuario</th><th className="px-3 py-2 text-left">Estado</th><th className="px-3 py-2 text-left">Acciones</th></tr></thead>
              <tbody>
                {emprendedoresFiltrados.map((e) => (
                  <tr key={e.comercio_id} className="border-t">
                    <td className="px-3 py-2">#{e.comercio_id}</td>
                    <td className="px-3 py-2"><div className="font-medium">{e.emprendedor?.nombreMarca || e.nombre}</div><div className="text-xs text-slate-500">{e.emprendedor?.rubro || '-'}</div></td>
                    <td className="px-3 py-2">{(e as any)?.emprendedor?.cuenta?.email || '-'}</td>
                    <td className="px-3 py-2">{e.activo ? 'Activo' : 'Inactivo'}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-2">
                        <button
                          className="rounded border px-2 py-1 disabled:opacity-60"
                          disabled={!!rowLoading[`emprendedor:${e.comercio_id}`]}
                          onClick={() => runRowAction(`emprendedor:${e.comercio_id}`, async () => {
                            await updateComercioMarket(e.comercio_id, { activo: true });
                            await load();
                          })}
                        >
                          {rowLoading[`emprendedor:${e.comercio_id}`] ? 'Procesando...' : 'Aprobar'}
                        </button>
                        <button
                          className="rounded border px-2 py-1 disabled:opacity-60"
                          disabled={!!rowLoading[`emprendedor:${e.comercio_id}`]}
                          onClick={() => runRowAction(`emprendedor:${e.comercio_id}`, async () => {
                            await updateComercioMarket(e.comercio_id, { activo: false });
                            await load();
                          })}
                        >
                          {rowLoading[`emprendedor:${e.comercio_id}`] ? 'Procesando...' : 'Quitar'}
                        </button>
                        <button className="rounded border px-2 py-1" onClick={() => {
                          setPasswordTarget({ comercioId: e.comercio_id, nombre: e.nombre });
                          setNewPassword('');
                          setShowPasswordModal(true);
                        }}>Editar password</button>
                        <button className="rounded bg-red-600 text-white px-2 py-1" onClick={() => setConfirmAction({
                          title: 'Eliminar usuario emprendedor',
                          message: `¿Seguro que querés eliminar ${e.nombre}? Se borrará su comercio y todos sus productos.`,
                          confirmText: 'Sí, eliminar usuario',
                          onConfirm: async () => {
                            await deleteEmprendedorAdminMarket(e.comercio_id);
                            await load();
                            setToast('Emprendedor, comercio y productos eliminados.');
                          },
                        })}>Eliminar usuario</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {confirmAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-4 shadow-xl space-y-3">
            <h3 className="text-lg font-semibold">{confirmAction.title}</h3>
            <p className="text-sm text-slate-600">{confirmAction.message}</p>
            <div className="flex justify-end gap-2">
              <button className="rounded border px-3 py-2" onClick={() => setConfirmAction(null)} disabled={busy}>Cancelar</button>
              <button className="rounded bg-red-600 text-white px-3 py-2" onClick={runConfirm} disabled={busy}>
                {busy ? 'Procesando...' : (confirmAction.confirmText || 'Confirmar')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPasswordModal && passwordTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-4 shadow-xl space-y-3">
            <h3 className="text-lg font-semibold">Editar password</h3>
            <p className="text-sm text-slate-600">Nueva contraseña para <b>{passwordTarget.nombre}</b></p>
            <input
              className="w-full rounded border p-2"
              type="password"
              placeholder="Mínimo 6 caracteres"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <button className="rounded border px-3 py-2" onClick={() => setShowPasswordModal(false)} disabled={busy}>Cancelar</button>
              <button className="rounded bg-blue-600 text-white px-3 py-2" onClick={submitPasswordReset} disabled={busy}>Guardar</button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-4 right-4 z-50 rounded bg-slate-900 px-4 py-2 text-sm text-white shadow">{toast}</div>
      )}
    </div>
  );
}
